import { useEffect, useRef, useState } from 'react';
import { Button, Card, CardContent, CardHeader, CardTitle, Label } from '@ah/design';
import { ArrowLeft, Camera, Mic, MicOff, CameraOff } from 'lucide-react';

interface DeviceSettings {
  cameraId?: string;
  micId?: string;
}

export function DeviceCheck({
  onContinue,
  onBack,
  joinLabel = 'Join',
}: {
  onContinue: (s: DeviceSettings) => void;
  onBack: () => void;
  /** Label for the join button. Defaults to "Join". */
  joinLabel?: string;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [mics, setMics] = useState<MediaDeviceInfo[]>([]);
  const [cameraId, setCameraId] = useState<string | undefined>();
  const [micId, setMicId] = useState<string | undefined>();
  const [camOn, setCamOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [micLevel, setMicLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // Request permission so enumerateDevices returns labels.
        const s = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        s.getTracks().forEach((t) => t.stop());
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (!active) return;
        setCameras(devices.filter((d) => d.kind === 'videoinput'));
        setMics(devices.filter((d) => d.kind === 'audioinput'));
      } catch (e) {
        setError('Please grant camera & microphone access to continue.');
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let disposed = false;
    let audioCtx: AudioContext | null = null;
    let rafId = 0;

    (async () => {
      if (!camOn && !micOn) return;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: camOn ? { deviceId: cameraId ? { exact: cameraId } : undefined } : false,
          audio: micOn ? { deviceId: micId ? { exact: micId } : undefined } : false,
        });
        if (disposed) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = stream;
        if (videoRef.current && camOn) videoRef.current.srcObject = stream;

        if (micOn && stream.getAudioTracks().length > 0) {
          audioCtx = new AudioContext();
          const src = audioCtx.createMediaStreamSource(stream);
          const analyser = audioCtx.createAnalyser();
          analyser.fftSize = 512;
          src.connect(analyser);
          const buf = new Uint8Array(analyser.fftSize);
          const tick = () => {
            analyser.getByteTimeDomainData(buf);
            let sum = 0;
            for (let i = 0; i < buf.length; i++) {
              const v = (buf[i]! - 128) / 128;
              sum += v * v;
            }
            const rms = Math.sqrt(sum / buf.length);
            setMicLevel(Math.min(1, rms * 3));
            rafId = requestAnimationFrame(tick);
          };
          tick();
        }
      } catch (e) {
        setError('Could not open that device.');
      }
    })();

    return () => {
      disposed = true;
      cancelAnimationFrame(rafId);
      audioCtx?.close();
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [camOn, micOn, cameraId, micId]);

  return (
    <div className="min-h-dvh grid place-items-center p-6 bg-background">
      <Card className="max-w-xl w-full">
        <CardHeader>
          <CardTitle className="text-2xl font-bold">Check your camera & microphone</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="aspect-video bg-black rounded-md overflow-hidden grid place-items-center">
            {camOn ? (
              <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
            ) : (
              <CameraOff size={36} className="text-white/40" />
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide">Mic level</Label>
            <div className="h-2 mt-1 bg-muted rounded overflow-hidden">
              <div
                className="h-full bg-green-500 transition-all"
                style={{ width: `${Math.round((micOn ? micLevel : 0) * 100)}%` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Camera</Label>
              <select
                className="w-full border rounded px-2 py-1.5 text-sm bg-background"
                value={cameraId ?? ''}
                onChange={(e) => setCameraId(e.target.value || undefined)}
              >
                <option value="">Default</option>
                {cameras.map((c) => (
                  <option key={c.deviceId} value={c.deviceId}>{c.label || 'Camera'}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Microphone</Label>
              <select
                className="w-full border rounded px-2 py-1.5 text-sm bg-background"
                value={micId ?? ''}
                onChange={(e) => setMicId(e.target.value || undefined)}
              >
                <option value="">Default</option>
                {mics.map((m) => (
                  <option key={m.deviceId} value={m.deviceId}>{m.label || 'Microphone'}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant={camOn ? 'secondary' : 'outline'} size="sm" onClick={() => setCamOn((v) => !v)}>
              {camOn ? <Camera size={14} /> : <CameraOff size={14} />} <span className="ml-1">{camOn ? 'Camera on' : 'Camera off'}</span>
            </Button>
            <Button variant={micOn ? 'secondary' : 'outline'} size="sm" onClick={() => setMicOn((v) => !v)}>
              {micOn ? <Mic size={14} /> : <MicOff size={14} />} <span className="ml-1">{micOn ? 'Mic on' : 'Mic off'}</span>
            </Button>
          </div>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </CardContent>
        <div className="flex justify-between p-6 pt-0">
          <Button variant="ghost" onClick={onBack}><ArrowLeft size={14} className="mr-1" /> Back</Button>
          <Button onClick={() => onContinue({ cameraId, micId })}>{joinLabel}</Button>
        </div>
      </Card>
    </div>
  );
}
